import { UploadingFloatingModal } from '@/components/dashboard/UploadingFloatingModal'
import { AskLinusSidebarLayout } from '@/components/ask-linus/AskLinusFab'
import { AddNameDialog } from '@/components/dashboard/AddNameDialog'
import { SessionTimeoutGuard } from '@/components/SessionTimeoutGuard'
import LoadingFullPage from '@/components/loading-ui/LoadingFullPage'
import {
  getAvailableTenantsForSuperAdmin,
  getIdTokenResultUsingQueryClient,
  getOrgPermissionsCurrentUser,
  getWhoAmI,
  queryKeys,
} from '@/data/queries'
import {
  hasAnyProjectEditorAccess,
  type PermissionExpression,
} from '@/data/entities'
import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
} from '@tanstack/react-router'

import { useSelectedTenantStore } from '@/stores/useSelectedTenantStore'
import { auth } from '@/lib/firebase'
import { USER_INFO_STALE_TIME } from '@/data/constants'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  AlertTriangleIcon,
  RefreshCwIcon,
  RotateCcwIcon,
  LogOutIcon,
} from 'lucide-react'

// Type definition for tenant information
type TenantInfo = {
  id: string
  name: string
  description: string
}

// Custom error component for tenant access issues - styled with LineWise theme
function TenantAccessError({
  error,
  onRetry,
}: {
  error: Error
  onRetry: () => void
}) {
  return (
    <div className="flex h-screen w-full flex-col overscroll-none">
      <main className="bg-background flex h-full w-full flex-1 flex-col items-center justify-center px-4">
        <div className="flex max-w-md min-w-[400px] flex-col items-start gap-8">
          {/* Logo */}
          <img src="/assets/logo.svg" alt="LineWise" className="h-15" />

          {/* Main Error Card */}
          <Card className="w-full">
            <CardHeader className="text-center">
              <div className="bg-destructive/10 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full">
                <AlertTriangleIcon className="text-destructive h-6 w-6" />
              </div>
              <CardTitle className="text-xl">Access Denied</CardTitle>
              <CardDescription className="text-base">
                {error.message}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Action Buttons */}
              <div className="space-y-3">
                <Button onClick={onRetry} className="w-full" size="lg">
                  <RefreshCwIcon className="mr-2 h-4 w-4" />
                  Refresh & Try Again
                </Button>

                <Button
                  variant="outline"
                  onClick={() => window.location.reload()}
                  className="w-full"
                  size="lg"
                >
                  <RotateCcwIcon className="mr-2 h-4 w-4" />
                  Reload Page
                </Button>

                <Button variant="outline" asChild className="w-full" size="lg">
                  <a href="/logout">
                    <LogOutIcon className="mr-2 h-4 w-4" />
                    Sign Out
                  </a>
                </Button>
              </div>

              {/* Help Message */}
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/20">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>Just invited?</strong> It may take 1-2 minutes for
                  your access to be fully activated. Please wait and then click
                  "Refresh & Try Again".
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="w-full text-center">
            <p className="text-muted-foreground text-xs">
              Need help? Contact your administrator or check the developer
              console for detailed logs.
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated')({
  component: RouteComponent,
  errorComponent: ({ error, reset }) => {
    if (error.message?.includes('You do not belong to an organization')) {
      return <TenantAccessError error={error} onRetry={reset} />
    }
    // For other errors, show default error component
    throw error
  },
  beforeLoad: async ({ context, location }) => {
    const { queryClient } = context
    // Read auth directly from Firebase — not from router context.
    const authState = auth.currentUser
    const idToken = await authState?.getIdToken()

    if (!authState || !idToken) {
      console.debug('redirect to login', location)
      throw redirect({ to: '/login', search: { redirectTo: location.href } })
    }

    // Check if the user's email is verified
    if (!authState.emailVerified && authState.email) {
      throw new Error(
        'Please verify your email then refresh the page to continue.',
      )
    }

    // Get ID token result for claims
    const idTokenResult = await getIdTokenResultUsingQueryClient({
      queryClient,
      authState,
    })

    const claims = idTokenResult.claims

    // Check if user is super admin
    const isSuperUser =
      (claims.systemRoles as string[])?.includes('admin') || false

    console.log('🔑 User authentication check:', {
      uid: authState.uid,
      email: authState.email,
      isSuperUser,
    })

    // Get available tenants based on user role
    let availableTenants: TenantInfo[]

    if (isSuperUser) {
      console.log('👑 Super admin detected - fetching all tenants')
      const tenants = await queryClient.ensureQueryData({
        queryKey: queryKeys.tenants.superAdmin(),
        queryFn: () => getAvailableTenantsForSuperAdmin({ token: idToken }),
        staleTime: USER_INFO_STALE_TIME,
      })
      availableTenants = tenants.map((tenant) => ({
        id: tenant.id,
        name: tenant.name,
        description: `Organization ${tenant.id}`,
      }))
    } else {
      console.log('👤 Regular user - fetching environment-specific tenants')
      const whoami = await queryClient.ensureQueryData({
        queryKey: queryKeys.users.whoami(),
        queryFn: () => getWhoAmI({ token: idToken }),
        staleTime: USER_INFO_STALE_TIME,
      })

      if (!whoami.user || !whoami.user.tenantInfos) {
        throw new Error(
          'You do not belong to an organization that has access to this application. If you were just invited, please wait 1-2 minutes and click "Refresh & Try Again". If the problem persists, contact your administrator.',
        )
      }

      availableTenants = whoami.user.tenantInfos.map((tenant) => ({
        id: tenant.id,
        name: tenant.name,
        description: `Organization ${tenant.name}`,
      }))
    }

    // Validate we have at least one tenant
    if (!availableTenants || availableTenants.length === 0) {
      throw new Error(
        'You do not belong to an organization that has access to this application. If you were just invited, please wait 1-2 minutes and click "Refresh & Try Again". If the problem persists, contact your administrator.',
      )
    }

    console.log('✅ User has access to tenants:', availableTenants)

    // Handle multi-tenant selection
    let TENANT_ID: string

    // Check if URL contains an orgId (e.g., /org/tenant-b/...)
    const orgIdMatch = location.pathname.match(/^\/org\/([^/]+)/)
    const urlOrgId = orgIdMatch ? orgIdMatch[1] : null

    // If URL specifies an orgId, validate and use it
    if (urlOrgId) {
      if (availableTenants.find((tenant) => tenant.id === urlOrgId)) {
        // User has access to this tenant - switch to it
        TENANT_ID = urlOrgId
        // Only update store if value changed — avoids triggering re-renders
        if (useSelectedTenantStore.getState().selectedTenantId !== TENANT_ID) {
          useSelectedTenantStore.getState().setSelectedTenantId(TENANT_ID)
          console.log('🔄 Switched to tenant from URL:', TENANT_ID)
        }
      } else {
        // User doesn't have access to this tenant
        console.warn('⛔ User does not have access to tenant:', urlOrgId)
        throw new Error(
          `You do not have access to organization "${urlOrgId}". Please contact your administrator if you believe this is an error.`,
        )
      }
    } else if (availableTenants.length > 1) {
      // Check if we're already on the tenant selection page
      if (location.pathname === '/select-tenant') {
        // For the tenant selection page, we don't need a specific tenant yet
        // Just return the available tenants for the selection UI

        return {
          authState,
          availableTenants,
          TENANT_ID: null,
          isOrgAdmin: false,
          isOrgEditor: false,
          isSysAdmin: false,
          hasAnyEditorAccess: false,
          permissionExpression: null,
        }
      }

      // Check if user has a selected tenant in localStorage
      const selectedTenantId =
        useSelectedTenantStore.getState().selectedTenantId

      if (
        selectedTenantId &&
        availableTenants.find((tenant) => tenant.id === selectedTenantId)
      ) {
        TENANT_ID = selectedTenantId
      } else {
        throw redirect({
          to: '/select-tenant',
          search: { redirectTo: location.href },
        })
      }
    } else {
      // Single tenant case
      // If user is on select-tenant page but only has one tenant, redirect them
      if (location.pathname === '/select-tenant') {
        throw redirect({
          to: '/org/$orgId/documents',
          params: { orgId: availableTenants[0].id },
        })
      }

      // Single tenant - use it directly and update the store
      TENANT_ID = availableTenants[0].id
      if (useSelectedTenantStore.getState().selectedTenantId !== TENANT_ID) {
        useSelectedTenantStore.getState().setSelectedTenantId(TENANT_ID)
      }
    }

    // Fetch the full permission expression and cache it.
    // Components using orgPermissionsCurrentUserOptions() share this cache key.
    let permissionExpression: PermissionExpression | null = null
    try {
      permissionExpression = await queryClient.ensureQueryData({
        queryKey: queryKeys.orgPermissions.currentUser(TENANT_ID),
        queryFn: () =>
          getOrgPermissionsCurrentUser({
            token: idToken,
            tenantId: TENANT_ID,
          }),
        staleTime: USER_INFO_STALE_TIME,
      })
    } catch (error) {
      console.warn('Failed to fetch org permissions:', error)
      permissionExpression = null
    }

    // Extract org-level role from the permission expression
    const orgRole =
      permissionExpression?.expressionTree?.root?.children?.org?.children?.[
        TENANT_ID
      ]?.role ?? null

    const isSysAdmin =
      (claims.systemRoles as string[])
        ?.map((role) => role.toLowerCase())
        ?.includes('admin') || false

    const isSysEditor =
      (claims.systemRoles as string[])
        ?.map((role) => role.toLowerCase())
        ?.includes('editor') || false

    const isOrgAdmin = isSysAdmin || orgRole === 'ADMIN'
    const isOrgEditor = isSysAdmin || isSysEditor || orgRole === 'EDITOR'
    const hasAnyEditorAccess =
      isOrgAdmin ||
      isOrgEditor ||
      hasAnyProjectEditorAccess(permissionExpression ?? undefined, TENANT_ID)

    return {
      authState,
      isOrgAdmin,
      isOrgEditor,
      isSysAdmin,
      hasAnyEditorAccess,
      permissionExpression,
      TENANT_ID,
      availableTenants,
    }
  },
  pendingComponent: LoadingFullPage,
})

function RouteComponent() {
  const location = useLocation()

  // Auth is guaranteed by beforeLoad (redirects to /login if not authenticated)

  // No sidebar on tenant selection page — no tenant context yet
  if (location.pathname === '/select-tenant') {
    return <Outlet />
  }

  return (
    <AskLinusSidebarLayout>
      <Outlet />
      <SessionTimeoutGuard />
      <AddNameDialog />
      <UploadingFloatingModal />
    </AskLinusSidebarLayout>
  )
}
